import React, { useContext, useState } from 'react';
import axiosClient from '@/api/axiosClient';
import API from '@/api/endpoints';
import { useT } from '@/i18n/labels';
import { useLangStore } from '@/stores/langStore';
import { useConversationStore } from '@/stores/conversationStore';
import { ConsoleContext } from '@/console/ConsoleContext';
import { mapSessionToConversation } from '@/utils/mapSession';
import type { Conversation, SessionDto } from '@/types';

/**
 * The ADR-017 conversation state (the tenant's own category axis), as an editable control.
 *
 * <b>This was display-only until now, by omission rather than by design.</b> The backend setter
 * <c>POST /api/sessions/{id}/category</c> was fully implemented — validation, pinning, SignalR
 * fan-out and all — and had no caller in any of the three React apps, so a tenant could author a
 * taxonomy in admin-portal that no agent could ever apply.
 *
 * <b>Options come from the store, never from a fetch.</b> ConsoleShell loads STATE_DEFS once and a
 * test asserts they are never re-requested; a per-conversation fetch here would quietly break that
 * and put one request per opened conversation on the wire.
 *
 * <b>The closed status is an option like any other</b> (conversation-status overhaul, 2026-11-02): every tenant has one
 * system status that closes, and choosing it CLOSES the conversation on the server; choosing any other status on a
 * CLOSED conversation REOPENS it (state back to NEW, owner cleared) and then applies that status. The server does the
 * work - this control only applies the session DTO it answers with (the state may have changed) and says why when the
 * server refuses a reopen (409 `reopen_window_passed` / `reopen_disabled`). The caller decides who may use it on a
 * closed conversation (the same gate the old Close button had).
 *
 * A dropdown rather than the chip row used by CrmCategoryPicker: this axis is single-valued and
 * sits inside a narrow key/value row, where a wrapping chip set would push the rest of the panel
 * around every time a tenant adds a state.
 */
export const ConversationCategoryPicker: React.FC<{
  conv: Conversation;
  /** Someone else owns this conversation (console v2): shown, not editable. */
  disabled?: boolean;
}> = ({ conv, disabled = false }) => {
  const { t } = useT();
  const lang = useLangStore((s) => s.lang);
  const stateDefs = useConversationStore((s) => s.stateDefs);
  const setConversationCategory = useConversationStore((s) => s.setConversationCategory);
  const applyRefreshedRow = useConversationStore((s) => s.applyRefreshedRow);
  // Optional on purpose (the card also renders where there is no shell): used only to re-read the row.
  const consoleCtx = useContext(ConsoleContext);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = stateDefs
    .filter((d) => d.isActive)
    .slice()
    .sort((a, b) => a.order - b.order);

  const byKey = new Map(options.map((d) => [d.key, d]));
  const defaultDef = options.find((d) => d.isDefault) ?? null;

  // The display rules are NOT invented here — they mirror what ConversationList uses to group rows,
  // and the panel showed the same before this became editable: a null category displays under the
  // tenant's `isDefault` def, and a key with no active def falls back to "Uncategorized". Diverging
  // would put a conversation in one directory while the panel named a different state.
  const activeDef = conv.category ? byKey.get(conv.category) ?? null : defaultDef;
  const unknownKey = Boolean(conv.category) && !byKey.has(conv.category!);

  // No vocabulary yet (defs still in flight, or a tenant whose seed has not run): fall back to the
  // read-only rendering rather than an empty dropdown that cannot do anything.
  if (options.length === 0) {
    return activeDef ? (
      <span className="ac-ctx-inline">
        <span className="ac-ctx-dot" style={{ background: activeDef.color }} />
        {activeDef.label[lang]}
      </span>
    ) : (
      <span className="ac-ctx-muted">{t('uncategorizedSection')}</span>
    );
  }

  const save = async (next: string) => {
    if (!next || next === conv.category) return;

    setSaving(true);
    setError(null);

    // Optimistic through the SAME store action the SignalR echo uses, so a local change and a
    // change made by a teammate take an identical path — including its re-sort, because changing
    // the category moves the row into a different inbox directory.
    const previous = conv.category ?? null;
    const requestedAt = Date.now();
    setConversationCategory(conv.sessionId, next);

    try {
      const res = await axiosClient.post<SessionDto | null>(API.SESSIONS.SET_CATEGORY(conv.sessionId), {
        category: next,
        origin: 'console',
      });
      // The server may have CLOSED or REOPENED the conversation with this change: the row it answers with is the truth.
      const dto = res?.data;
      if (dto && typeof dto === 'object' && typeof dto.sessionId === 'string' && dto.sessionId.toLowerCase() === conv.sessionId.toLowerCase()) {
        applyRefreshedRow(mapSessionToConversation(dto), requestedAt);
      } else {
        // No row in the answer: the state may still have moved, so ask the server.
        void consoleCtx?.refreshRow(conv.sessionId);
      }
    } catch (err) {
      setConversationCategory(conv.sessionId, previous);
      const code = (err as { response?: { status?: number; data?: { code?: unknown } } } | null)?.response;
      const reason = code?.status === 409 && typeof code.data?.code === 'string' ? code.data.code : null;
      setError(
        reason === 'reopen_window_passed'
          ? t('conv_category_reopen_window_passed')
          : reason === 'reopen_disabled'
            ? t('conv_category_reopen_disabled')
            : t('conv_category_save_failed'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <span className="ac-ctx-inline" style={{ gap: 6, flexWrap: 'wrap' }}>
      {activeDef && <span className="ac-ctx-dot" style={{ background: activeDef.color }} />}

      <select
        className="ac-ctx-select"
        // Matches what the row DISPLAYS, so the control never contradicts the label beside it. A
        // consequence worth knowing: when the category is null the default def shows as selected,
        // so re-picking it fires no change event and cannot explicitly pin it. That ambiguity
        // predates this control — the read-only chip showed the default def for a null category too.
        value={conv.category ?? defaultDef?.key ?? ''}
        disabled={saving || disabled}
        onChange={(e) => void save(e.target.value)}
        aria-label={t('conv_state_category')}
        // Stated, not hidden: a manual set pins the category, so the server stops re-categorising
        // this conversation on later turns.
        title={t('conv_category_pin_hint')}
        data-testid="conv-category-select"
        style={{
          fontSize: 12,
          fontFamily: 'inherit',
          color: 'var(--text)',
          background: 'transparent',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '2px 6px',
          maxWidth: 150,
          cursor: saving || disabled ? 'default' : 'pointer',
          opacity: saving || disabled ? 0.6 : 1,
        }}
      >
        {/* A stored key with no ACTIVE def — the tenant disabled or deleted that state while this
            conversation still carries it. Kept as a visible, selectable-away entry rather than
            silently rendering blank, which would misreport the conversation as uncategorised.
            There is deliberately no "clear" option: the endpoint requires a value, so offering one
            would produce a 400 the agent cannot act on. */}
        {unknownKey && (
          <option value={conv.category!} disabled>
            {t('uncategorizedSection')}
          </option>
        )}

        {options.map((d) => (
          <option key={d.key} value={d.key}>
            {d.label[lang]}
          </option>
        ))}
      </select>

      {error && (
        <span
          data-testid="conv-category-error"
          style={{ color: 'var(--danger, #c0392b)', fontSize: 11 }}
        >
          {error}
        </span>
      )}
    </span>
  );
};
